import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { ResetGetUserDetails } from "store/actions/Settings/GetUserDetails";
import { ManageUser } from "store/actions/Settings/ManageUser";
import { GetGroups } from "store/actions/GetGroups";
import { GetRoles } from "store/actions/Rbac";
import { showSnackbar } from "store/actions/Utility";

import { usePermissions } from "components/Hook";
import { hasPermission } from "hooks/usePermission";
import CustomButton from "components/common/Button";
import { AlertType } from "components/common/Snackbar";

import { assignMembers } from "services/groups";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AddUser from "components/Screens/SettingV2/AddUser/AddUserV2";
import UserItem from "./UserItem";
import MoveToGroupDialog from "./MoveToGroupDialog";
import RemoveUser from "components/Screens/SettingV2/UserPermissions/RemoveUserV2";

function Index() {
  const dispatch = useDispatch();
  const { loggedInUserType } = usePermissions();

  const [addorEditUser, setAddorEditUser] = useState({
    open: false,
    mode: null,
  });
  const [customerId, setCustomerId] = useState("");
  const [editableUser, setEditAbleUser] = useState({});
  const [openRemoveUser, setOpenRemoveUser] = useState(false);
  const [moveUser, setMoveUser] = useState(null);
  const [isMovingUser, setIsMovingUser] = useState(false);

  const loggedInUser = useMemo(() => JSON.parse(localStorage.getItem("loggedInUser")), []);

  const manageUser = useSelector((state) => state.ManageUser.manageUser);
  const { groups } = useSelector((state) => state.GetGroups || {});
  const { roles } = useSelector((state) => state.Rbac || {});

  const groupNameById = useMemo(() => {
    const m = {};
    (groups || []).forEach((g) => {
      m[g.Group_ID] = g.Name;
    });
    return m;
  }, [groups]);

  // Group picker options for the move dialog. The Main group (Is_Main) is a valid target so users
  // can be moved back to Main; label it so it is obvious in the list.
  const groupOptions = useMemo(
    () => (groups || []).map((g) => ({ value: g.Group_ID, label: g.Is_Main ? `${g.Name} (Main)` : g.Name })),
    [groups]
  );

  const roleNameById = useMemo(() => {
    const m = {};
    (roles || []).forEach((r) => {
      m[r.Role_ID] = r.Name;
    });
    return m;
  }, [roles]);

  const getAccountOwnerAndUsers = (users) => {
    if (!users) return { accountOwner: null, currentUser: null, otherUsers: [] };

    const accountOwner = users.find((user) => user.mainUser === true);
    const currentUser = users.find((user) => user.email === loggedInUser?.email);
    const otherUsers = users.filter((user) => !user.mainUser && user.email !== loggedInUser?.email);

    return { accountOwner, currentUser, otherUsers };
  };

  const handleRemoveUser = (user) => {
    setOpenRemoveUser(true);
    setEditAbleUser(user);
  };

  const handleCloseRemoveUser = () => {
    setOpenRemoveUser(false);
    setEditAbleUser({});
  };

  const handleMoveUser = (user) => {
    setMoveUser(user);
  };

  const handleCloseMoveUser = () => {
    setMoveUser(null);
  };

  // Reassign the selected user to the chosen group via the guarded members endpoint. On success,
  // refresh the user list (so the new group shows) and the groups tree (so counts update); on
  // failure surface the backend's message (owner-must-stay-in-Main / out-of-scope / self-reassign).
  const handleSubmitMoveUser = async (targetGroupId) => {
    if (!moveUser || !targetGroupId) return;
    setIsMovingUser(true);
    try {
      const res = await assignMembers(targetGroupId, [
        { principalType: "customerUser", principalId: moveUser.userId },
      ]);
      if (res && res.ok) {
        dispatch(showSnackbar({ type: AlertType.success, message: "User moved successfully", dispatch }));
        setMoveUser(null);
        const userAdditionalInfo = JSON.parse(sessionStorage.getItem("userAdditionalInfo"));
        fetchUserList(userAdditionalInfo);
        dispatch(GetGroups({ customerId: userAdditionalInfo?.customerId }));
      } else {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: res?.payload?.message || "Failed to move user",
            dispatch,
          })
        );
      }
    } catch (error) {
      dispatch(showSnackbar({ type: AlertType.error, message: "Failed to move user", dispatch }));
    } finally {
      setIsMovingUser(false);
    }
  };

  const fetchUserList = useCallback(
    (userAdditionalInfo) => {
      let manageUserRequest = "";
      if (
        !addorEditUser.open &&
        userAdditionalInfo &&
        Object.keys(userAdditionalInfo).length &&
        Object.keys(loggedInUser).length &&
        loggedInUser.userId
      ) {
        if (loggedInUser?.type == "customer") {
          manageUserRequest = `v2/customer/${userAdditionalInfo?.customerId}/customerUsers`;
        } else if (loggedInUser?.type == "edge") {
          manageUserRequest = "users";
        }

        dispatch(
          ManageUser({
            request: manageUserRequest,
            rowsPerPage: 50,
            page: 0,
            sortBy: {
              field: "First_Name",
              direction: "asc",
            },
          })
        );
      }
    },
    [addorEditUser]
  );

  useEffect(() => {
    const userAdditionalInfo = JSON.parse(sessionStorage.getItem("userAdditionalInfo"));
    setCustomerId(userAdditionalInfo?.customerId);
    fetchUserList(userAdditionalInfo);
    dispatch(ResetGetUserDetails({}));
    dispatch(GetGroups({ customerId: userAdditionalInfo?.customerId }));
    // Roles only label each user's role; gate the fetch on the permission so customers
    // (who no longer hold roles.read) don't trigger a guaranteed 403.
    if (hasPermission("roles.read") && !(roles || []).length) {
      dispatch(GetRoles());
    }
  }, [addorEditUser]);

  const { accountOwner, currentUser, otherUsers } = manageUser?.ok
    ? getAccountOwnerAndUsers(manageUser.payload.list)
    : { accountOwner: null, currentUser: null, otherUsers: [] };

  // Gate the create-user control by the active context: managing edge employees
  // uses edgeUsers.create, managing customer users uses userManagement.create.
  const canCreateUser =
    loggedInUserType === "edge" ? hasPermission("edgeUsers.create") : hasPermission("userManagement.create");

  return (
    <div>
      {addorEditUser.open === false ? (
        <div>
          <div className="flex justify-between">
            <div className="heading-h3-semibold">Current Users</div>
            {canCreateUser && (
              <CustomButton
                size="large"
                onClick={() =>
                  setAddorEditUser({
                    open: true,
                    mode: "addUser",
                  })
                }
                startIcon={<AddOutlinedIcon />}
                label={loggedInUserType !== "edge" ? "Add New User" : "Add Edge Employee"}
              />
            )}
          </div>
          <div className="users-list">
            {accountOwner && (
              <UserItem
                user={accountOwner}
                groupName={groupNameById[accountOwner.groupId] || "—"}
                roleName={roleNameById[accountOwner.roleId]}
                loggedInUser={loggedInUser}
                setEditAbleUser={setEditAbleUser}
                setAddorEditUser={setAddorEditUser}
                handleRemoveUser={handleRemoveUser}
                handleMoveUser={handleMoveUser}
              />
            )}
            {currentUser && currentUser.email !== accountOwner?.email && (
              <UserItem
                user={currentUser}
                groupName={groupNameById[currentUser.groupId] || "—"}
                roleName={roleNameById[currentUser.roleId]}
                loggedInUser={loggedInUser}
                setEditAbleUser={setEditAbleUser}
                setAddorEditUser={setAddorEditUser}
                handleRemoveUser={handleRemoveUser}
                handleMoveUser={handleMoveUser}
              />
            )}
            {otherUsers.map((user) => (
              <UserItem
                key={user.userId}
                user={user}
                groupName={groupNameById[user.groupId] || "—"}
                roleName={roleNameById[user.roleId]}
                loggedInUser={loggedInUser}
                setEditAbleUser={setEditAbleUser}
                setAddorEditUser={setAddorEditUser}
                handleRemoveUser={handleRemoveUser}
                handleMoveUser={handleMoveUser}
              />
            ))}
          </div>
          <RemoveUser
            openRemoveUser={openRemoveUser}
            setOpenRemoveUser={setOpenRemoveUser}
            editableUser={editableUser}
            handleCloseRemoveUser={handleCloseRemoveUser}
          />
          <MoveToGroupDialog
            open={!!moveUser}
            user={moveUser}
            groupOptions={groupOptions}
            isSubmitting={isMovingUser}
            onClose={handleCloseMoveUser}
            onSubmit={handleSubmitMoveUser}
          />
        </div>
      ) : (
        <AddUser
          navigatedFor={addorEditUser.mode}
          paramId={addorEditUser.mode === "editUser" ? editableUser.userId : null}
          addorEditUser={addorEditUser}
          editableUser={addorEditUser.mode === "editUser" ? editableUser : null}
          setAddorEditUser={setAddorEditUser}
          managersList={manageUser?.ok ? manageUser.payload.list : []}
          customerId={customerId}
        />
      )}
    </div>
  );
}

export default Index;
